import React, {
  FC,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  SLIDE_DURATION_MS,
  SLIDE_EASING,
} from '../../../shared/slideTransition/slideTransition';

/**
 * Clearance kept above the sheet so it never runs under the status bar or
 * notch, and the height of the chrome sitting above this panel — the sheet's
 * `HeaderBar` (61px) plus the tab bar (48px in a 8px band). Together these
 * cap the panel so the sheet as a whole stops at `100dvh - 96px`.
 *
 * `dvh` is the visible viewport (it shrinks with mobile browser chrome). `vh`
 * is the fallback: on iOS Safari it is the large viewport, which is why a
 * `vh`-only cap can run the header off-screen in a mobile browser while the
 * native webview still looks fine.
 */
const TOP_CLEARANCE_PX = 96;
const HEADER_BAR_PX = 61;
const TAB_BAR_PX = 64;
const CHROME_BELOW_CLEARANCE_PX = TOP_CLEARANCE_PX + HEADER_BAR_PX + TAB_BAR_PX;

/**
 * Scrolls its own content once the sheet reaches its cap, so the sheet never
 * grows past it.
 */
const TransitionOuter = styled.div`
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-height: calc(100vh - ${CHROME_BELOW_CLEARANCE_PX}px);
  max-height: calc(100dvh - ${CHROME_BELOW_CLEARANCE_PX}px);
  transition: height ${SLIDE_DURATION_MS}ms ${SLIDE_EASING};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

interface SheetPanelTransitionProps {
  /** Identifies the current content; scroll resets when it changes. */
  contentKey?: string;
  onScrolledChange?: (_scrolled: boolean) => void;
  children: ReactNode;
}

/**
 * Sizes the sheet's panel to its content and animates between sizes with the
 * same motion as `SlideUpTransition` — switching tabs, or opening and closing
 * a waypoint group. `height: auto` is not interpolatable, so the measured
 * height is tracked and applied instead, bounded by the sheet's cap.
 */
const SheetPanelTransition: FC<SheetPanelTransitionProps> = ({
  contentKey,
  onScrolledChange,
  children,
}) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>();

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return undefined;
    }

    const measure = () =>
      setContentHeight(content.getBoundingClientRect().height);
    measure();

    // jsdom and older webviews have no ResizeObserver; the height just stays
    // at whatever the first measurement was, which still renders correctly.
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer || !onScrolledChange) {
      return undefined;
    }

    const handleScroll = () => onScrolledChange(outer.scrollTop > 0);
    outer.addEventListener('scroll', handleScroll);
    return () => outer.removeEventListener('scroll', handleScroll);
  }, [onScrolledChange]);

  // A new tab starts at the top, and with no leftover header shadow.
  useEffect(() => {
    if (outerRef.current) {
      outerRef.current.scrollTop = 0;
    }
    if (onScrolledChange) {
      onScrolledChange(false);
    }
  }, [contentKey, onScrolledChange]);

  return (
    <TransitionOuter
      ref={outerRef}
      data-testid="map-settings-panel"
      style={
        contentHeight === undefined
          ? undefined
          : { height: `${contentHeight}px` }
      }
    >
      <div ref={contentRef}>{children}</div>
    </TransitionOuter>
  );
};

export default SheetPanelTransition;
